import { Injectable } from '@angular/core';
import { Firestore, collection, query, orderBy, limit, getDocs, Timestamp, docData, doc, updateDoc } from '@angular/fire/firestore';
import { COLLECTION_USER_V1, StateService, Systems } from 'ngx-core-services';
import { first } from 'rxjs/operators';

export const COLLECTION_ACCOUNT_MIGRATION = 'sunflower_migrations';

export interface MigrationDoc {
  createdAt: Timestamp;
  migration: {
    firstDocId: string;
    message?: string;
    status: 'pending' | 'migrated' | 'error' | 'completed';
    level: 'Silver' | 'Gold' | 'Platinum';
    email: string;
    docIds?: string[];
  }
  systems: Systems;
}

@Injectable({
  providedIn: 'root'
})
export class AccountMigrationService {
  readonly user$ = this.stateService.user$;

  constructor(
    private readonly stateService: StateService,
    private readonly afs: Firestore,
  ) { }

  async accountMigrationDetails(userId: string): Promise<MigrationDoc | null> {
    const systems: Systems = [];

    const accountMigrationRef = collection(this.afs, COLLECTION_USER_V1, userId, COLLECTION_ACCOUNT_MIGRATION);
    const q = query(accountMigrationRef, orderBy('createdAt', 'desc'), limit(1));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const latestDoc = querySnapshot.docs[0];
    const latestDocData = latestDoc.data() as MigrationDoc;
    const firstDocId = latestDocData.migration.firstDocId;

    const firstDocRef = doc(this.afs, COLLECTION_USER_V1, userId, COLLECTION_ACCOUNT_MIGRATION, firstDocId);
    const firstDocData = await docData(firstDocRef).pipe(first()).toPromise() as MigrationDoc;

    const migration = firstDocData.migration;
    systems.push(...firstDocData.systems);

    if (firstDocData.migration.docIds && firstDocData.migration.docIds.length) {
      const childDocs = await Promise.all(
        firstDocData.migration.docIds.map((id) => {
          const childDocRef = doc(this.afs, COLLECTION_USER_V1, userId, COLLECTION_ACCOUNT_MIGRATION, id);
          return docData(childDocRef).pipe(first()).toPromise() as Promise<MigrationDoc>;
        })
      );
      childDocs.forEach((childDoc) => {
        systems.push(...childDoc.systems);
      });
    }

    return {
      createdAt: firstDocData.createdAt,
      migration,
      systems,
    };
  }

  async completeLatestMigration(userId: string, docId: string): Promise<void> {
    const firstDocRef = doc(this.afs, COLLECTION_USER_V1, userId, COLLECTION_ACCOUNT_MIGRATION, docId);
    const firstDocData = await docData(firstDocRef).pipe(first()).toPromise() as MigrationDoc;

    const migration = {
      ...firstDocData.migration,
      status: 'completed',
    };

    await updateDoc(firstDocRef, {
      migration,
    });
  }
}
